import React, { useState, useEffect } from 'react';
import {
  Radio,
  BookOpen,
  Users,
  GraduationCap,
  TrendingUp,
  X,
  ArrowRight,
  Sparkles
} from 'lucide-react';
import './TutorialTour.css';

const tutorialSteps = [
  {
    id: 'welcome',
    title: 'Welcome to HyperDrive! 🎉',
    description: "Let's take a quick tour to help you get started on your trading journey.",
    target: null, // No specific target - centered modal
    icon: Sparkles,
    position: 'center'
  },
  {
    id: 'live-stream',
    title: 'Join Live Sessions',
    description: 'Watch Coach CJ trade live every weekday at 9:20 AM ET. Learn real-time strategies and ask questions during the session.',
    target: '[data-tour="live-stream"]',
    icon: Radio,
    position: 'right'
  },
  {
    id: 'journal',
    title: 'Track Your Trades',
    description: 'Log every trade in your journal. Track your entries, exits, and P&L to identify patterns and improve your performance.',
    target: '[data-tour="journal"]',
    icon: BookOpen,
    position: 'right'
  },
  {
    id: 'community',
    title: 'Connect with Community',
    description: 'Chat with fellow traders, share ideas, and get support from the HyperDrive community. We\'re all here to grow together!',
    target: '[data-tour="community"]',
    icon: Users,
    position: 'right'
  },
  {
    id: 'courses',
    title: 'Learn & Grow',
    description: 'Access trading courses and educational content to sharpen your skills. From basics to advanced strategies.',
    target: '[data-tour="courses"]',
    icon: GraduationCap,
    position: 'right'
  },
  {
    id: 'performance',
    title: 'Analyze Performance',
    description: 'View your trading statistics, win rate, and equity curve. Use data to make better trading decisions.',
    target: '[data-tour="performance"]',
    icon: TrendingUp,
    position: 'right'
  },
  {
    id: 'complete',
    title: "You're All Set! 🚀",
    description: "You now know the essentials. Start by joining a live session or logging your first trade. Happy trading!",
    target: null,
    icon: Sparkles,
    position: 'center'
  }
];

const TutorialTour = ({ onComplete, onSkip }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const [highlightPosition, setHighlightPosition] = useState(null);

  const step = tutorialSteps[currentStep];
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === tutorialSteps.length - 1;
  const isCenteredStep = step.position === 'center';

  useEffect(() => {
    if (step.target) {
      const targetElement = document.querySelector(step.target);
      if (targetElement) {
        const rect = targetElement.getBoundingClientRect();
        
        // Highlight position
        setHighlightPosition({
          top: rect.top - 4,
          left: rect.left - 4,
          width: rect.width + 8,
          height: rect.height + 8
        });

        // Tooltip position (to the right of the element)
        setTooltipPosition({
          top: rect.top + rect.height / 2,
          left: rect.right + 20
        });

        // Scroll element into view if needed
        targetElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    } else {
      setHighlightPosition(null);
    }
  }, [currentStep, step.target]);

  const handleNext = () => {
    if (isLastStep) {
      onComplete();
    } else {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSkip = () => {
    onSkip();
  };

  const Icon = step.icon;

  return (
    <div className="tutorial-overlay">
      {/* Backdrop */}
      <div className="tutorial-backdrop" />

      {/* Highlight box around target element */}
      {highlightPosition && (
        <div 
          key={`highlight-${currentStep}`}
          className="tutorial-highlight"
          style={{
            top: highlightPosition.top,
            left: highlightPosition.left,
            width: highlightPosition.width,
            height: highlightPosition.height
          }}
        />
      )}

      {/* Tooltip */}
      <div 
        className={`tutorial-tooltip ${isCenteredStep ? 'centered' : ''}`}
        style={!isCenteredStep ? {
          top: tooltipPosition.top,
          left: tooltipPosition.left,
          transform: 'translateY(-50%)'
        } : {}}
      >
        {/* Skip button */}
        {!isLastStep && (
          <button className="tutorial-skip" onClick={handleSkip}>
            <X size={18} />
          </button>
        )}

        {/* Icon */}
        <div className={`tutorial-icon ${isLastStep || isFirstStep ? 'celebration' : ''}`}>
          <Icon size={32} />
        </div>

        {/* Content */}
        <h3 className="tutorial-title">{step.title}</h3>
        <p className="tutorial-description">{step.description}</p>

        {/* Progress dots */}
        <div className="tutorial-progress">
          {tutorialSteps.map((_, index) => (
            <div 
              key={index} 
              className={`progress-dot ${index === currentStep ? 'active' : ''} ${index < currentStep ? 'completed' : ''}`}
            />
          ))}
        </div>

        {/* Navigation */}
        <div className="tutorial-nav">
          {currentStep > 0 && !isFirstStep && (
            <button className="tutorial-btn secondary" onClick={handlePrevious}>
              Back
            </button>
          )}
          <button className="tutorial-btn primary" onClick={handleNext}>
            {isLastStep ? "Let's Go!" : isFirstStep ? "Start Tour" : "Next"}
            {!isLastStep && <ArrowRight size={16} />}
          </button>
        </div>

        {/* Step counter */}
        <div className="tutorial-counter">
          {currentStep + 1} of {tutorialSteps.length}
        </div>
      </div>
    </div>
  );
};

export default TutorialTour;
